// import axios from "../shared/axios';
import { api as axios } from "../shared/axios";
import useSWR from "swr";
import { API_URL } from "../shared/config";

const fetcher = (url) => {
  // Don't make request if URL contains null/undefined
  if (!url || url.includes("null") || url.includes("undefined")) {
    return Promise.resolve({ data: null });
  }
  return axios.get(url);
};

export const useLoggedInUser = () => {
  const { data, error, mutate } = useSWR(
    `${API_URL}/account/profile/`,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 50000,
    }
  );
  return {
    user: data?.data,
    isLoadingUser: !error && !data,
    isErrorUser: error,
    mutateUser: mutate,
  };
};

export const useSignedInUser = () => {
  const { data, error, mutate } = useSWR(
    `${API_URL}/account/profile/v2/`,
    fetcher
  );
  return {
    userDetails: data?.data,
    isLoadingUserDetails: !error && !data,
    isErrorUserDetails: error,
    mutateUserDetails: mutate,
  };
};

export const useUserProfile = () => {
  const { data, error, mutate } = useSWR(
    `${API_URL}/account/profile/users/`,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: true, // Keep this for network recovery
      dedupingInterval: 10000,
      focusThrottleInterval: 10000,
      errorRetryCount: 3,
    }
  );
  return {
    userProfile: data?.data,
    isLoadingUserProfile: !error && !data,
    isErrorUserProfile: error,
    mutateUserProfile: mutate,
  };
};

export const usePlantsList = (companyId) => {
  const { data, error, mutate } = useSWR(
    `${API_URL}/account/profile/plants/`,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: true, // Keep this for network recovery
    }
  );

  if (companyId) {
    // it contains a list of companies and plants belonging to that user
    const filtered = data?.data?.find((company) => company?.id === companyId);
    return {
      plantsList: filtered,
      isLoadingPlantsList: !error && !data,
      isErrorPlantsList: error,
      mutatePlantsList: mutate,
    };
  }
  return {
    plantsList: data?.data,
    isLoadingPlantsList: !error && !data,
    isErrorPlantsList: error,
    mutatePlantsList: mutate,
  };
};

export const usePlantsListById = (id) => {
  const { data, error, mutate } = useSWR(
    id ? `${API_URL}/account/profile/plant/${id}/` : null,
    fetcher,
    {
      dedupingInterval: 10000,
    }
  );
  return {
    plantsListById: data?.data,
    isLoadingPlantsListById: !error && !data && !!id,
    isErrorPlantsListById: error,
    mutatePlantsListById: mutate,
  };
};

export const useIsAuthenticated = () => {
  const { data, error } = useSWR(
    `${API_URL}/account/is-authenticated/`,
    fetcher
  );
  return {
    isAuthenticated: data?.data?.is_authenticated,
    isLoading: !error && !data?.data?.is_authenticated,
    isErrorUser: error,
  };
};

export const usePlantMemberList = (id) => {
  const { data, error, mutate } = useSWR(
    `${API_URL}/plant/${id}/member/`,
    fetcher
  );
  return {
    memberList: data?.data?.sort((a, b) => (a.groups > b.groups ? 1 : -1)),
    isLoadingMemberList: !error && !data,
    isErrorMemberList: error,
    mutateMemberList: mutate,
  };
};

export const removeMembers = async (id) => {
  try {
    const response = await axios.delete(`${API_URL}/role/${id}/`);
    return { data: response.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const registerNewUser = async (data, queryParam) => {
  try {
    const endpoint = queryParam
      ? `${API_URL}/account/register/${queryParam}`
      : `${API_URL}/account/register/`;

    // const request = await axios.post(`${API_URL}/account/register/`, data);
    const request = await axios.post(endpoint, data);
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const microsoftAuthentication = async (data) => {
  try {
    const request = await axios.post(`${API_URL}/account/social/microsoft/`, {
      access_token: data,
    });
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const SignInAndRegisterGoogle = async (data) => {
  try {
    const request = await axios.post(`${API_URL}/account/social/google/`, {
      auth_token: data,
    });
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const sendVerify = async (data) => {
  try {
    const request = await axios.post(
      `${API_URL}/account/register/sendEmailVerify/`,
      data
    );
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const verifyOtpEmail = async (data) => {
  try {
    const request = await axios.post(
      `${API_URL}/account/register/emailVerify/`,
      data
    );
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const inviteUser = async (data) => {
  try {
    const request = await axios.post(`${API_URL}/account/invite/`, data);
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};
export const useGetInvitationDetails = (_pathname, _email) => {
  let email = new URLSearchParams(_email).get("email");
  let invitationId = _pathname?.split("/")?.slice(-2)?.[0];

  const { data, error } = useSWR(
    `${API_URL}/account/invite/${invitationId}/${email}/`,
    fetcher
  );
  return {
    invitation: data?.data,
    isLoadingInvitation: !error && !data?.data,
    isErrorInvitation: error,
  };
};

export const userInvitationConfirmation = async (invitationId, data) => {
  try {
    const request = await axios.post(
      `${API_URL}/account/invite/${invitationId}/verify/`,
      data
    );
    return { data: request?.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error?.response?.data?.error };
  }
};

export const updateUserRole = async (data) => {
  try {
    const request = await axios.post(`${API_URL}/role/edit/`, data);
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const useGetRoleGroupsList = () => {
  const { data, error, mutate } = useSWR(
    `${API_URL}/role/groups/simple/`,
    fetcher
  );
  return {
    roleGroupsList: data?.data,
    isLoadingRoleGroupsList: !error && !data?.data,
    isErrorRoleGroupsList: error,
    mutateRoleGroupsList: mutate,
  };
};

export const getSiteList = async () => {
  try {
    const response = await axios.get(`${API_URL}/plant`);
    return { data: response.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const useGetDevice = (id) => {
  const { data, error, mutate } = useSWR(
    `${API_URL}/plant/${id}/device/`,
    fetcher
  );
  return {
    deviceList: data?.data,
    isLoadingDeviceList: !error && !data?.data,
    isErrorDeviceList: error,
    mutateDeviceList: mutate,
  };
};

export const useGetServer = (id) => {
  const { data, error, mutate } = useSWR(
    `${API_URL}/plant/${id}/server/`,
    fetcher
  );
  return {
    serverList: data?.data,
    isLoadingServerList: !error && !data?.data,
    isErrorServerList: error,
    mutateServerList: mutate,
  };
};

export const createNewDevice = async (data) => {
  try {
    const request = await axios.post(`${API_URL}/device/`, data);
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const createNewServer = async (plantId, formData) => {
  try {
    const request = await axios.post(
      `${API_URL}/plant/${plantId}/server/add/`,
      formData
    );
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error.response.data.error };
  }
};

export const useGetPlace = (id, type = "other") => {
  const { data, error } = useSWR(`${API_URL}/plant/${id}/building/`, fetcher);
  const place = (type = "deviceSetup"
    ? data?.data?.building?.map((b) => ({
        value: b?.building_id,
        label: b?.building_name?.replace(/_/g, " "),
        ...b,
      }))
    : data?.data);
  return {
    getPlace: place,
    isLoadingGetPlace: !error && !data?.data,
    isErrorGetPlace: error,
  };
};

export const useGetDeviceType = (id) => {
  const { data, error } = useSWR(`${API_URL}/device/device_type/`, fetcher);
  return {
    getDeviceType: data?.data,
    isLoadingGetDeviceType: !error && !data?.data,
    isErrorGetDeviceType: error,
  };
};

export const useGetDeviceName = (devTypeId) => {
  const { data, error } = useSWR(
    `${API_URL}/thirdparty/huawei/devices/property/${devTypeId}/`,
    fetcher
  );
  return {
    getDeviceName: data?.data,
    isLoadingGetDeviceType: !error && !data?.data,
    isErrorGetDeviceType: error,
  };
};

export const editServer = async (plantId, formData) => {
  try {
    const request = await axios.post(
      `${API_URL}/plant/${plantId}/server/edit/`,
      formData
    );
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error.response.data.error };
  }
};

// export const editDevice = async (deviceId,data) => {
//     try {
//         const request = await axios.patch(`${API_URL}/device/${deviceId}/`,data);
//         return { data: request.data, error: undefined };
//     } catch (error) {
//         return { data: undefined, error: error };
//     }
// }

export const requestResetPassword = async (data) => {
  try {
    const request = await axios.post(
      `${API_URL}/account/password/request_reset_password/`,
      data
    );
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const resetPassword = async (data) => {
  try {
    const request = await axios.post(
      `${API_URL}/account/password/reset_password/`,
      data
    );
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const changePassword = async (data) => {
  try {
    const request = await axios.post(
      `${API_URL}/account/change/password/`,
      data
    );
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const verifyPasswordResetToken = async (token) => {
  try {
    const request = await axios.get(
      `${API_URL}/account/password/reset_password/${token}/`
    );
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const useGetBrand = (type = "other") => {
  const { data, error } = useSWR(
    `${API_URL}/device/brand/model/${
      type === "vehicle" ? "?type=vehicle" : ""
    }`,
    fetcher
  );
  let brands = [];

  if (type == "deviceSetup") {
    brands = data?.data
      ?.map((brand) => ({
        value: brand.id,
        label: brand.name,
        id: brand.id,
        model: brand.model,
      }))
      ?.filter((brand) => !brand.label.toLowerCase().includes("huawei"));
  } else {
    brands = data?.data?.map((brand) => ({
      value: brand.id,
      label: brand.name,
      id: brand.id,
      model: brand.model,
    }));
  }
  return {
    brandList: brands,
    isLoadingBrandList: !error && !brands,
    isErrorBrandList: error,
  };
};

export const useGetFloorPlanDevice = (id) => {
  const { data, error } = useSWR(
    `${API_URL}/plant/${id}/device/group/`,
    fetcher
  );
  return {
    getFloorPlanDevice: data?.data,
    isLoadingGetFloorPlanDevice: !error && !data?.data,
    isErrorGetFloorPlanDevice: error,
  };
};

export const useGetDeviceDetail = (id) => {
  const { data, error } = useSWR(`${API_URL}/device/${id}/info/`, fetcher);
  return {
    getDeviceDetail: data?.data,
    isLoadingGetDeviceDetail: !error && !data?.data,
    isErrorDeviceDetail: error,
  };
};
export const useGetDeviceControlDetails = (id) => {
  const { data, error } = useSWR(
    `${API_URL}/device/${id}/control/list/`,
    fetcher
  );
  return {
    controlDetails: data?.data,
    isLoadingControlDetails: !error && !data?.data,
    isErrorControlDetails: error,
  };
};

export const deviceControl = async (id, commandData) => {
  try {
    const request = await axios.post(
      `${API_URL}/device/${id}/control/`,
      commandData
    );
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error?.response?.data?.error };
  }
};

export const removeDevices = async (id) => {
  try {
    await axios.delete(`${API_URL}/device/${id}/`);
  } catch (error) {
    alert(error);
  }
};

export const removeServer = async (id, server) => {
  try {
    const request = await axios.post(
      `${API_URL}/plant/${id}/server/remove/`,
      server
    );
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error };
  }
};

export const editProfile = async (id, data) => {
  try {
    const request = await axios.patch(
      `${API_URL}/account/profile/${id}/`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return { data: request.data, error: undefined };
  } catch (error) {
    return { data: undefined, error: error?.response?.data?.error };
  }
};
